import { Building, Calendar, MapPin, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../../components/common/Navbar";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Domain, Job } from "../../swagger";
import { candidateApi, domainApi } from "../../utils/configuration";
import { useLocation } from "react-router-dom";

interface Filters {
  search: string;
  domainId: string;
  categoryId: string;
  subCategoryId: string;
  experienceLevel: string;
  jobType: string;
  location: string;
  page: number;
  limit: number;
}

export default function JobListPage() {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [domains, setDomains] = useState<Domain[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState<Filters>({
    search: "",
    domainId: "all",
    categoryId: "all",
    subCategoryId: "all",
    experienceLevel: "all",
    jobType: "all",
    location: "",
    page: 1,
    limit: 10,
  });

  // Debounced search
  const [searchDebounce, setSearchDebounce] = useState("");
  const [locationDebounce, setLocationDebounce] = useState("");

  // Get selected domain and category for dependent dropdowns
  const selectedDomain = domains.find(
    (d) => d.id?.toString() === filters.domainId
  );
  const selectedCategory = selectedDomain?.Categories?.find(
    (c) => c.id?.toString() === filters.categoryId
  );

  const navigate = useNavigate();

  const location = useLocation();

  // Utility function to get search param value by name
  const getQueryParam = (param: string) => {
    const params = new URLSearchParams(location.search);
    return params.get(param) || "";
  };

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchDebounce, page: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchDebounce]);

  // Debounce location input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => ({ ...prev, location: locationDebounce, page: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [locationDebounce]);

  // Reset dependent fields when parent changes
  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      categoryId: "all",
      subCategoryId: "all",
      page: 1,
    }));
  }, [filters.domainId]);

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      subCategoryId: "all",
      page: 1,
    }));
  }, [filters.categoryId]);

  // Fetch jobs function
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const query: any = {
        page: filters.page,
        limit: filters.limit,
        status: "PUBLISHED",
      };

      if (filters.search.trim()) {
        query.title = filters.search.trim();
      }
      if (filters.experienceLevel !== "all") {
        query.experienceLevel = filters.experienceLevel;
      }
      if (filters.jobType !== "all") {
        query.jobType = filters.jobType;
      }
      if (filters.domainId !== "all") {
        query.domainId = filters.domainId;
      }
      if (filters.categoryId !== "all") {
        query.categoryId = filters.categoryId;
      }
      if (filters.subCategoryId !== "all") {
        query.subCategoryId = filters.subCategoryId;
      }
      if (filters.location.trim()) {
        query.location = filters.location.trim();
      }

      const data = await candidateApi.candidateGetAllJobs(query);
      if (!data) {
        throw new Error("Failed to fetch jobs");
      }
      setJobs(data.data!);
      setPagination({
        page: data.page!,
        limit: data.limit!,
        total: data.total!,
        totalPages: data.totalPages!,
      });
    } catch (error) {
      console.error("Error fetching jobs:", error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Fetch domains 
  const fetchDomains = useCallback(async () => {
    try {
      const fetchedDomains = await domainApi.getAllDomains();
      if (!fetchedDomains) {
        throw new Error("Failed to fetch domains");
      }
      setDomains(fetchedDomains);

      // --- Auto-filter domain based on query param ---
      const domainQueryParam = getQueryParam("domain").toLowerCase();
      if (domainQueryParam) {
        // Try to match domain name, case insensitive
        const matched = fetchedDomains.find(
          (domain) => domain.title?.toLowerCase().includes(domainQueryParam)
        );
        setFilters((prev) => ({
          ...prev,
          domainId: matched && domainQueryParam.length > 0 ? matched.id!.toString() : "all",
          page: 1,
        }));
      }
    } catch (error) {
      console.error("Error fetching domains:", error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // Initial data fetch
  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  useEffect(() => {
    // Only run when domains are loaded or location changes
    if (domains.length > 0) {
      const domainQueryParam = getQueryParam("domain").toLowerCase();
      const matched = domains.find(
        (domain) => domain.title?.toLowerCase().includes(domainQueryParam)
      );
      setFilters((prev) => ({
        ...prev,
        domainId: matched && domainQueryParam.length > 0 ? matched.id!.toString() : "all",
        page: 1,
      }));
    }
    // eslint-disable-next-line
  }, [domains, location.search]);


  // Fetch jobs when filters change
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handleJobClick = (jobId: number) => {
    navigate(`/jobs/${jobId}`);
  };

  const getJobTypeLabel = (jobType: string) => {
    const labels = {
      FULLTIME: "Full-time",
      PARTTIME: "Part-time",
      CONTRACT: "Contract",
      FREELANCE: "Freelance",
      INTERNSHIP: "Internship",
    };
    return labels[jobType as keyof typeof labels] || jobType;
  };

  const getExperienceLevelLabel = (level: string) => {
    const labels = {
      ENTRY: "Entry Level",
      MID: "Mid Level",
      SENIOR: "Senior Level",
      LEAD: "Lead Level",
      EXECUTIVE: "Executive Level",
    };
    return labels[level as keyof typeof labels] || level;
  };

  const formatSalary = (
    minSalary?: number,
    maxSalary?: number,
    currency?: string
  ) => {
    if (!minSalary && !maxSalary) return "Salary not specified";
    const curr = currency || "USD";
    if (minSalary && maxSalary) {
      return `${curr} ${minSalary.toLocaleString()} - ${maxSalary.toLocaleString()}`;
    }
    if (minSalary) {
      return `${curr} ${minSalary.toLocaleString()}+`;
    }
    return `Up to ${curr} ${maxSalary!.toLocaleString()}`;
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "Posted today";
    if (diffDays <= 7) return `Posted ${diffDays} days ago`;
    return `Posted ${date.toLocaleDateString()}`;
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-white to-cyan-50 -z-50"></div>
          <div className="absolute top-20 right-10 w-72 h-72 bg-gradient-to-br from-indigo-200 to-purple-200 rounded-full opacity-20 blur-3xl animate-float -z-50"></div>
          <div className="absolute bottom-10 left-10 w-64 h-64 bg-gradient-to-tr from-cyan-200 to-blue-200 rounded-full opacity-20 blur-2xl animate-float-delayed -z-50"></div>

          <div className="mb-8">
            <h1 className="text-3xl font-bold text-indigo-700 mb-2">
              Find Your Dream Job
            </h1>
            <p className="text-gray-600">
              Discover opportunities that match your skills and interests
            </p>
          </div>

          {/* Filters Section */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="">Filter Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              {/* First Row of Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search jobs or companies..."
                    value={searchDebounce}
                    onChange={(e) => setSearchDebounce(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Experience Level */}
                <Select
                  value={filters.experienceLevel}
                  onValueChange={(value) =>
                    handleFilterChange("experienceLevel", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Experience Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Experience Levels</SelectItem>
                    <SelectItem value="ENTRY">Entry Level</SelectItem>
                    <SelectItem value="MID">Mid Level</SelectItem>
                    <SelectItem value="SENIOR">Senior Level</SelectItem>
                    <SelectItem value="LEAD">Lead Level</SelectItem>
                    <SelectItem value="EXECUTIVE">Executive Level</SelectItem>
                  </SelectContent>
                </Select>

                {/* Job Type */}
                <Select
                  value={filters.jobType}
                  onValueChange={(value) =>
                    handleFilterChange("jobType", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Job Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Job Types</SelectItem>
                    <SelectItem value="FULLTIME">Full-time</SelectItem>
                    <SelectItem value="PARTTIME">Part-time</SelectItem>
                    <SelectItem value="CONTRACT">Contract</SelectItem>
                    <SelectItem value="FREELANCE">Freelance</SelectItem>
                    <SelectItem value="INTERNSHIP">Internship</SelectItem>
                  </SelectContent>
                </Select>

                {/* Location */}
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Location"
                    value={locationDebounce}
                    onChange={(e) => setLocationDebounce(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Second Row - Category Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Domain */}
                <Select
                  value={filters.domainId}
                  onValueChange={(value) =>
                    handleFilterChange("domainId", value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Domain" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Domains</SelectItem>
                    {domains.map((domain) => (
                      <SelectItem
                        key={domain.id}
                        value={domain.id?.toString() ?? ""}
                      >
                        {domain.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Category */}
                <Select
                  value={filters.categoryId}
                  onValueChange={(value) =>
                    handleFilterChange("categoryId", value)
                  }
                  disabled={filters.domainId === "all"}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {selectedDomain?.Categories?.map((category) => (
                      <SelectItem
                        key={category.id}
                        value={category.id?.toString() ?? ""}
                      >
                        {category.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Subcategory */}
                <Select
                  value={filters.subCategoryId}
                  onValueChange={(value) =>
                    handleFilterChange("subCategoryId", value)
                  }
                  disabled={filters.categoryId === "all"}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Subcategories</SelectItem>
                    {selectedCategory?.SubCategories?.map((subcategory) => (
                      <SelectItem
                        key={subcategory.id}
                        value={subcategory.id?.toString() ?? ""}
                      >
                        {subcategory.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-gray-600">
              {loading ? "Loading..." : `${pagination.total} active jobs found`}
            </p>
          </div>

          {/* Jobs List */}
          <div className="space-y-4">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-gray-500 text-lg">Loading jobs...</div>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">
                  No jobs found matching your criteria.
                </p>
                <p className="text-gray-400">
                  Try adjusting your filters to see more results.
                </p>
              </div>
            ) : (
              jobs.map((job) => (
                <Card
                  key={job.id}
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                >
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div
                          onClick={() => handleJobClick(job.id!)}
                          className="block"
                        >
                          <h3 className="text-xl font-semibold text-indigo-700 hover:text-blue-600 mb-2">
                            {job.title}
                          </h3>
                        </div>

                        <div className="flex items-center gap-4 text-gray-600 mb-3 flex-wrap">
                          <div className="flex items-center gap-1">
                            <Building className="h-4 w-4" />
                            <span>{job.companyName}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <MapPin className="h-4 w-4" />
                            <span>{job.location}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            {/* <DollarSign className="h-4 w-4" /> */}
                            <span>
                              {formatSalary(
                                job.salaryStart,
                                job.salaryEnd,
                                "₹"
                              )}
                            </span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 mb-3 flex-wrap">
                          <Badge variant="secondary">
                            {getExperienceLevelLabel(job.experienceLevel!)}
                          </Badge>
                          <Badge variant="outline">
                            {getJobTypeLabel(job.type!)}
                          </Badge>
                          {job.type && <Badge variant="outline">Remote</Badge>}
                        </div>

                        <div className="text-sm text-gray-600 mb-3">
                          <div className="font-medium">{job.Domain?.title}</div>
                          {job.Category && (
                            <div className="text-gray-500">
                              {job.Category.title}
                              {job.SubCategory && ` • ${job.SubCategory.title}`}
                            </div>
                          )}
                        </div>

                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {job.description}
                        </p>

                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(job.createdAt!)}</span>
                        </div>
                      </div>

                      <div className="ml-6">
                        <Button
                          onClick={() => handleJobClick(job.id!)}
                          className="bg-indigo-600 text-white hover:bg-indigo-700"
                        >
                          Apply Now
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center mt-8 space-x-2">
              <Button
                variant="outline"
                onClick={() =>
                  handleFilterChange(
                    "page",
                    Math.max(1, filters.page - 1).toString()
                  )
                }
                disabled={filters.page <= 1}
              >
                Previous
              </Button>

              <div className="flex items-center space-x-1">
                {Array.from(
                  { length: Math.min(5, pagination.totalPages) },
                  (_, i) => {
                    let pageNumber: number;
                    if (pagination.totalPages <= 5) {
                      pageNumber = i + 1;
                    } else if (pagination.page <= 3) {
                      pageNumber = i + 1;
                    } else if (pagination.page >= pagination.totalPages - 2) {
                      pageNumber = pagination.totalPages - 4 + i;
                    } else {
                      pageNumber = pagination.page - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNumber}
                        variant={
                          pagination.page === pageNumber ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() =>
                          handleFilterChange("page", pageNumber.toString())
                        }
                        className="w-8 h-8 p-0"
                      >
                        {pageNumber}
                      </Button>
                    );
                  }
                )}
              </div>

              <Button
                variant="outline"
                onClick={() =>
                  handleFilterChange(
                    "page",
                    Math.min(pagination.totalPages, filters.page + 1).toString()
                  )
                }
                disabled={filters.page >= pagination.totalPages}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
